(function(root, factory) {
  'use strict';

  var api = factory(root || {});
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.CelovendoCoverage = api;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null), function(windowRef) {
  'use strict';

  var OWN_ATTRIBUTE_KEYS = [
    'Latitude',
    'Longitude',
    'delivery_method',
    'delivery_zone',
    'delivery_coverage_status'
  ];
  var EPSILON = 1e-10;
  // Topology checks use a sweep over segment bounding boxes. This cap turns
  // pathological, highly-overlapping input into an explicit invalid geometry
  // instead of doing an unbounded quadratic amount of work on municipalities.
  var MAX_TOPOLOGY_COMPARISONS = 500000;

  function error(message) {
    return new Error('CelovendoCoverage: ' + message);
  }

  function isNumber(value) {
    return typeof value === 'number' && isFinite(value);
  }

  function validPoint(lng, lat) {
    return isNumber(lng) && isNumber(lat) && lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;
  }

  function samePosition(a, b) {
    return Math.abs(a[0] - b[0]) <= EPSILON && Math.abs(a[1] - b[1]) <= EPSILON;
  }

  function validatePosition(position) {
    return Array.isArray(position) && position.length >= 2 && validPoint(position[0], position[1]);
  }

  function validateRing(ring, checkTopology) {
    if (!Array.isArray(ring) || ring.length < 4) return false;
    for (var i = 0; i < ring.length; i++) {
      if (!validatePosition(ring[i])) return false;
    }
    if (!samePosition(ring[0], ring[ring.length - 1])) return false;
    var area = 0;
    for (var p = 0; p < ring.length - 1; p++) {
      if (samePosition(ring[p], ring[p + 1])) return false;
      area += ring[p][0] * ring[p + 1][1] - ring[p + 1][0] * ring[p][1];
    }
    // A closed line is not a Polygon ring. The tolerance is deliberately far
    // below practical map precision, while still rejecting collinear points.
    if (Math.abs(area) <= EPSILON) return false;
    return checkTopology === false || !ringSelfIntersects(ring);
  }

  function validatePolygonCoordinates(coordinates, checkTopology) {
    if (!Array.isArray(coordinates) || !coordinates.length) return false;
    for (var i = 0; i < coordinates.length; i++) {
      if (!validateRing(coordinates[i], checkTopology)) return false;
    }
    if (checkTopology === false) return true;
    var outer = coordinates[0];
    for (var hole = 1; hole < coordinates.length; hole++) {
      // Holes must be strictly inside the exterior. A hole touching an edge
      // makes boundary semantics ambiguous and is rejected rather than
      // silently changing the deliverable area.
      if (ringPosition(coordinates[hole][0][0], coordinates[hole][0][1], outer) !== 2 ||
          ringsIntersect(coordinates[hole], outer)) return false;
      for (var previous = 1; previous < hole; previous++) {
        if (ringsIntersect(coordinates[hole], coordinates[previous]) ||
            ringPosition(coordinates[hole][0][0], coordinates[hole][0][1], coordinates[previous]) ||
            ringPosition(coordinates[previous][0][0], coordinates[previous][0][1], coordinates[hole])) return false;
      }
    }
    return true;
  }

  function segmentBounds(a, b) {
    return {
      minX: Math.min(a[0], b[0]),
      maxX: Math.max(a[0], b[0]),
      minY: Math.min(a[1], b[1]),
      maxY: Math.max(a[1], b[1])
    };
  }

  function rangesOverlap(aMin, aMax, bMin, bMax) {
    return aMax >= bMin - EPSILON && bMax >= aMin - EPSILON;
  }

  function orientation(a, b, c) {
    var value = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
    return Math.abs(value) <= EPSILON ? 0 : (value > 0 ? 1 : -1);
  }

  function onSegment(a, point, b) {
    return orientation(a, b, point) === 0 &&
      rangesOverlap(a[0], b[0], point[0], point[0]) &&
      rangesOverlap(a[1], b[1], point[1], point[1]);
  }

  function segmentsIntersect(a, b, c, d) {
    var ab = segmentBounds(a, b);
    var cd = segmentBounds(c, d);
    if (!rangesOverlap(ab.minX, ab.maxX, cd.minX, cd.maxX) ||
        !rangesOverlap(ab.minY, ab.maxY, cd.minY, cd.maxY)) return false;
    var o1 = orientation(a, b, c);
    var o2 = orientation(a, b, d);
    var o3 = orientation(c, d, a);
    var o4 = orientation(c, d, b);
    if (o1 !== o2 && o3 !== o4) return true;
    return (o1 === 0 && onSegment(a, c, b)) ||
      (o2 === 0 && onSegment(a, d, b)) ||
      (o3 === 0 && onSegment(c, a, d)) ||
      (o4 === 0 && onSegment(c, b, d));
  }

  function makeSegments(ring, group) {
    var segments = [];
    for (var i = 0; i < ring.length - 1; i++) {
      var bounds = segmentBounds(ring[i], ring[i + 1]);
      bounds.a = ring[i];
      bounds.b = ring[i + 1];
      bounds.index = i;
      bounds.group = group;
      segments.push(bounds);
    }
    return segments;
  }

  function ringSelfIntersects(ring) {
    var segments = makeSegments(ring, 0).sort(function(a, b) { return a.minX - b.minX; });
    var active = [];
    var comparisons = 0;
    for (var i = 0; i < segments.length; i++) {
      var segment = segments[i];
      for (var prune = active.length - 1; prune >= 0; prune--) {
        if (active[prune].maxX < segment.minX - EPSILON) active.splice(prune, 1);
      }
      for (var a = 0; a < active.length; a++) {
        var other = active[a];
        // Neighbouring segments share their endpoint by definition.
        if (Math.abs(segment.index - other.index) === 1 ||
            (segment.index === 0 && other.index === segments.length - 1) ||
            (other.index === 0 && segment.index === segments.length - 1)) continue;
        if (++comparisons > MAX_TOPOLOGY_COMPARISONS) return true;
        if (rangesOverlap(segment.minY, segment.maxY, other.minY, other.maxY) &&
            segmentsIntersect(segment.a, segment.b, other.a, other.b)) return true;
      }
      active.push(segment);
    }
    return false;
  }

  function ringsIntersect(first, second) {
    var segments = makeSegments(first, 0).concat(makeSegments(second, 1));
    segments.sort(function(a, b) { return a.minX - b.minX; });
    var active = [];
    var comparisons = 0;
    for (var i = 0; i < segments.length; i++) {
      var segment = segments[i];
      for (var prune = active.length - 1; prune >= 0; prune--) {
        if (active[prune].maxX < segment.minX - EPSILON) active.splice(prune, 1);
      }
      for (var a = 0; a < active.length; a++) {
        var other = active[a];
        if (segment.group === other.group) continue;
        if (++comparisons > MAX_TOPOLOGY_COMPARISONS) return true;
        if (rangesOverlap(segment.minY, segment.maxY, other.minY, other.maxY) &&
            segmentsIntersect(segment.a, segment.b, other.a, other.b)) return true;
      }
      active.push(segment);
    }
    return false;
  }

  /*
   * This deliberately only accepts the geometry types this service can
   * classify.  Silently accepting a GeometryCollection, for example, would
   * make a malformed coverage configuration look like an empty delivery area.
   */
  function validateGeometry(geometry, checkTopology) {
    if (!geometry || typeof geometry !== 'object') return false;
    if (geometry.type === 'Polygon') return validatePolygonCoordinates(geometry.coordinates, checkTopology);
    if (geometry.type === 'MultiPolygon') {
      if (!Array.isArray(geometry.coordinates) || !geometry.coordinates.length) return false;
      for (var i = 0; i < geometry.coordinates.length; i++) {
        if (!validatePolygonCoordinates(geometry.coordinates[i], checkTopology)) return false;
      }
      return true;
    }
    return false;
  }

  function validation(value) {
    var valid = validateGeoJSON(value);
    return { valid: valid, error: valid ? null : 'Expected valid Polygon, MultiPolygon, Feature, or FeatureCollection GeoJSON.' };
  }

  function validateGeoJSON(value, checkTopology) {
    if (!value || typeof value !== 'object') return false;
    if (value.type === 'Feature') return validateGeometry(value.geometry, checkTopology);
    if (value.type === 'FeatureCollection') {
      if (!Array.isArray(value.features) || !value.features.length) return false;
      for (var i = 0; i < value.features.length; i++) {
        if (!value.features[i] || value.features[i].type !== 'Feature' || !validateGeometry(value.features[i].geometry, checkTopology)) return false;
      }
      return true;
    }
    return validateGeometry(value, checkTopology);
  }

  function asFeatureCollection(value, checkTopology) {
    if (!validateGeoJSON(value, checkTopology)) throw error('Invalid GeoJSON geometry.');
    if (value.type === 'FeatureCollection') return value;
    if (value.type === 'Feature') return { type: 'FeatureCollection', features: [value] };
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: value }]
    };
  }

  // 0 = outside, 1 = boundary, 2 = inside. Boundary is intentional coverage.
  function ringPosition(lng, lat, ring) {
    var inside = false;
    for (var i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      var a = ring[j];
      var b = ring[i];
      var cross = (lng - a[0]) * (b[1] - a[1]) - (lat - a[1]) * (b[0] - a[0]);
      var withinX = lng >= Math.min(a[0], b[0]) - EPSILON && lng <= Math.max(a[0], b[0]) + EPSILON;
      var withinY = lat >= Math.min(a[1], b[1]) - EPSILON && lat <= Math.max(a[1], b[1]) + EPSILON;
      if (Math.abs(cross) <= EPSILON && withinX && withinY) return 1;

      if ((a[1] > lat) !== (b[1] > lat)) {
        var atX = (b[0] - a[0]) * (lat - a[1]) / (b[1] - a[1]) + a[0];
        if (lng < atX) inside = !inside;
      }
    }
    return inside ? 2 : 0;
  }

  var ZONE_COLOR = '#1d4ed8';
  var ZONE_LABEL = 'Zona de entrega con mensajero';

  /*
   * Punto del borde más cercano a `center` ({ lat, lng }) entre varios anillos
   * de puntos { lat, lng } cerrados. Aproxima en plano, con la longitud escalada
   * por el coseno de la latitud: basta para colocar una etiqueta en pantalla.
   */
  function closestEdgePoint(rings, center) {
    if (!rings || !center) return null;
    var k = Math.cos(center.lat * Math.PI / 180) || 1;
    var best = null;
    rings.forEach(function(ring) {
      for (var i = 0; i < ring.length - 1; i++) {
        var ax = (ring[i].lng - center.lng) * k;
        var ay = ring[i].lat - center.lat;
        var dx = (ring[i + 1].lng - center.lng) * k - ax;
        var dy = ring[i + 1].lat - center.lat - ay;
        var length = dx * dx + dy * dy;
        var t = length ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / length)) : 0;
        var px = ax + t * dx;
        var py = ay + t * dy;
        var distance = px * px + py * py;
        if (!best || distance < best.distance) {
          best = { lat: center.lat + py, lng: center.lng + px / k, distance: distance };
        }
      }
    });
    return best ? { lat: best.lat, lng: best.lng } : null;
  }

  function polygonContains(lng, lat, coordinates) {
    var outer = ringPosition(lng, lat, coordinates[0]);
    if (!outer) return false;
    // A hole's perimeter belongs to the hole, not to the deliverable area.
    for (var i = 1; i < coordinates.length; i++) {
      if (ringPosition(lng, lat, coordinates[i])) return false;
    }
    return true;
  }

  function geometryContainsUnchecked(lng, lat, geometry) {
    if (geometry.type === 'Polygon') return polygonContains(lng, lat, geometry.coordinates);
    for (var i = 0; i < geometry.coordinates.length; i++) {
      if (polygonContains(lng, lat, geometry.coordinates[i])) return true;
    }
    return false;
  }

  function geometryContains(lng, lat, geometry, checkTopology) {
    if (!validPoint(lng, lat) || !validateGeometry(geometry, checkTopology)) return false;
    return geometryContainsUnchecked(lng, lat, geometry);
  }

  function geometryBounds(geometry) {
    var minLng = Infinity;
    var maxLng = -Infinity;
    var minLat = Infinity;
    var maxLat = -Infinity;
    var polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
    polygons.forEach(function(polygon) {
      polygon.forEach(function(ring) {
        ring.forEach(function(position) {
          minLng = Math.min(minLng, position[0]);
          maxLng = Math.max(maxLng, position[0]);
          minLat = Math.min(minLat, position[1]);
          maxLat = Math.max(maxLat, position[1]);
        });
      });
    });
    return { minLng: minLng, maxLng: maxLng, minLat: minLat, maxLat: maxLat };
  }

  // Country geometries are structurally validated once at load time and
  // indexed by bounding box. Pin movements therefore do not re-run topology
  // validation (or scan every municipality's vertices).
  function containsCountry(lng, lat, countryIndex) {
    if (!validPoint(lng, lat) || !countryIndex) return false;
    for (var i = 0; i < countryIndex.length; i++) {
      var entry = countryIndex[i];
      var bounds = entry.bounds;
      if (lng < bounds.minLng || lng > bounds.maxLng || lat < bounds.minLat || lat > bounds.maxLat) continue;
      if (geometryContainsUnchecked(lng, lat, entry.geometry)) return true;
    }
    return false;
  }

  /*
   * Public coordinate order is GeoJSON order: contains(lng, lat, geojson).
   * The object and [lng, lat] forms are useful when passing a map result
   * without accidentally swapping its coordinates.
   */
  function contains(first, second, third) {
    var lat;
    var lng;
    var geojson;
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      geojson = first;
      lng = second;
      lat = third;
    } else if (Array.isArray(first) && first.length >= 2 && third === undefined) {
      lng = first[0];
      lat = first[1];
      geojson = second;
    } else {
      lng = first;
      lat = second;
      geojson = third;
    }
    if (!validPoint(lng, lat) || !geojson) return false;
    if (geojson.type === 'Feature') return geometryContains(lng, lat, geojson.geometry);
    if (geojson.type === 'FeatureCollection') {
      if (!Array.isArray(geojson.features)) return false;
      for (var i = 0; i < geojson.features.length; i++) {
        if (geojson.features[i] && geometryContains(lng, lat, geojson.features[i].geometry)) return true;
      }
      return false;
    }
    return geometryContains(lng, lat, geojson);
  }

  function getConfigFromDocument(doc) {
    if (!doc || !doc.getElementById) return {};
    var node = doc.getElementById('cv-coverage-config');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || node.innerText || '{}');
    } catch (parseError) {
      throw error('The #cv-coverage-config JSON is invalid.');
    }
  }

  function hasCustomSource(value) {
    if (value === undefined || value === null) return false;
    return typeof value !== 'string' || value.trim() !== '';
  }

  function parseInline(value, label) {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (parseError) {
        throw error(label + ' inline GeoJSON is invalid JSON.');
      }
    }
    if (!value || typeof value !== 'object') throw error(label + ' inline GeoJSON is invalid.');
    return value;
  }

  function clone(value) {
    if (value === undefined) return undefined;
    return JSON.parse(JSON.stringify(value));
  }

  function createCoverageEngine(config, dependencies) {
    dependencies = dependencies || {};
    var doc = dependencies.document || windowRef.document;
    var configurationError = null;
    if (!config) {
      try {
        config = getConfigFromDocument(doc);
      } catch (configError) {
        configurationError = configError;
        config = {};
      }
    }
    var request = dependencies.fetch || windowRef.fetch;
    var localeRoot = dependencies.localeRoot;
    if (localeRoot === undefined) localeRoot = (windowRef.theme && windowRef.theme.localeRootUrl) || '';
    var loadPromise = null;
    var loaded = null;
    var overlaysByMap = typeof WeakMap !== 'undefined' ? new WeakMap() : [];
    var revision = 0;
    var state = freshState(null, revision);
    var readyByRevision = {};
    var attributesByRevision = {};
    var latestWanted = null;
    var saving = false;
    var lastSavedRevision = -1;
    var saveErrors = {};
    var saveWaiters = [];

    function freshState(location, currentRevision) {
      return {
        location: location || null,
        coverage: {
          delivery_method: 'pendiente',
          delivery_zone: 'desconocida',
          delivery_coverage_status: 'unknown'
        },
        delivery_method: 'pendiente',
        delivery_zone: 'desconocida',
        delivery_coverage_status: 'unknown',
        revision: currentRevision,
        saving: false,
        saveError: null
      };
    }

    function notify() {
      var snapshot = getState();
      if (doc && doc.dispatchEvent) {
        var event;
        try {
          event = new windowRef.CustomEvent('cv:coverage-changed', { detail: snapshot });
        } catch (e) {
          event = doc.createEvent('CustomEvent');
          event.initCustomEvent('cv:coverage-changed', true, true, snapshot);
        }
        doc.dispatchEvent(event);
      }
      // A waiter can re-register itself while a newer revision is pending.
      // Always drain a snapshot, otherwise iterating the live array can grow
      // without end during cv:coverage-changed notifications.
      notifySaveWaiters();
    }

    function setCoverage(coverage) {
      state.coverage = coverage;
      state.delivery_method = coverage.delivery_method;
      state.delivery_zone = coverage.delivery_zone;
      state.delivery_coverage_status = coverage.delivery_coverage_status;
    }

    function cartAttributes(location, coverage) {
      var attributes = {
        delivery_method: coverage.delivery_method,
        delivery_zone: coverage.delivery_zone,
        delivery_coverage_status: coverage.delivery_coverage_status
      };
      if (location && location.confirmed && location.precise && validPoint(location.lng, location.lat)) {
        attributes.Latitude = String(location.lat);
        attributes.Longitude = String(location.lng);
      } else {
        // Blank values remove stale exact coordinates without touching unrelated
        // cart attributes; /cart/update.js merges this partial object.
        attributes.Latitude = '';
        attributes.Longitude = '';
      }
      return attributes;
    }

    function notifySaveWaiters() {
      var waiters = saveWaiters.slice();
      saveWaiters = [];
      for (var i = 0; i < waiters.length; i++) waiters[i]();
    }

    function postAttributes(attributes) {
      if (typeof request !== 'function') return Promise.reject(error('fetch is unavailable; cart attributes cannot be saved.'));
      return Promise.resolve(request(localeRoot + '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: attributes })
      })).then(function(response) {
        if (!response || !response.ok) throw error('Cart update failed' + (response && response.status ? ' (HTTP ' + response.status + ')' : '') + '.');
        return response;
      });
    }

    function runSaveQueue() {
      if (saving || !latestWanted) return;
      var job = latestWanted;
      latestWanted = null;
      saving = true;
      if (job.revision === revision) {
        state.saving = true;
        state.saveError = null;
        notify();
      }
      postAttributes(job.attributes).then(function() {
        lastSavedRevision = Math.max(lastSavedRevision, job.revision);
        delete saveErrors[job.revision];
        if (job.revision === revision) {
          state.saving = false;
          state.saveError = null;
          notify();
        } else {
          notifySaveWaiters();
        }
      }).catch(function(saveError) {
        saveErrors[job.revision] = saveError;
        if (job.revision === revision) {
          state.saving = false;
          state.saveError = saveError.message;
          notify();
        } else {
          notifySaveWaiters();
        }
      }).then(function() {
        saving = false;
        if (latestWanted) runSaveQueue();
      });
    }

    function queueSave(currentRevision) {
      latestWanted = {
        revision: currentRevision,
        attributes: attributesByRevision[currentRevision]
      };
      runSaveQueue();
    }

    function commit(currentRevision, location, coverage) {
      if (currentRevision !== revision) return;
      state.location = location;
      setCoverage(coverage);
      state.saving = false;
      state.saveError = null;
      attributesByRevision[currentRevision] = cartAttributes(location, coverage);
      queueSave(currentRevision);
      notify();
    }

    function sourceForZone(zoneName) {
      var zones = config.zones || {};
      var zone = zones[zoneName] || {};
      if (hasCustomSource(zone.content)) return { type: 'inline', value: zone.content };
      if (hasCustomSource(zone.url)) return { type: 'url', value: zone.url };
      if (hasCustomSource(zone.fallback)) return { type: 'fallback', value: zone.fallback };
      throw error('No GeoJSON source was configured for zone "' + zoneName + '".');
    }

    function fetchJSON(url, label) {
      if (typeof request !== 'function') return Promise.reject(error('fetch is unavailable while loading ' + label + '.'));
      return Promise.resolve(request(url, { credentials: 'same-origin' })).then(function(response) {
        if (!response || !response.ok) throw error('Unable to load ' + label + (response && response.status ? ' (HTTP ' + response.status + ')' : '') + '.');
        return response.json();
      });
    }

    function loadSource(source, label) {
      if (source.type === 'inline') {
        try {
          return Promise.resolve(parseInline(source.value, label));
        } catch (inlineError) {
          return Promise.reject(inlineError);
        }
      }
      if (typeof source.value === 'object') return Promise.resolve(source.value);
      return fetchJSON(source.value, label);
    }

    function countrySource() {
      if (hasCustomSource(config.countryContent)) return { type: 'inline', value: config.countryContent };
      if (hasCustomSource(config.countryUrl)) return { type: 'url', value: config.countryUrl };
      // Kept as compatibility aliases for a theme config emitted before the
      // final countryUrl setting name was introduced.
      if (hasCustomSource(config.municipalities)) return { type: 'url', value: config.municipalities };
      if (config.country && hasCustomSource(config.country.url)) return { type: 'url', value: config.country.url };
      throw error('countryUrl is required to verify Cargo Expreso coverage.');
    }

    function verifyFeatureCollection(value, label, checkTopology) {
      try {
        return asFeatureCollection(value, checkTopology);
      } catch (geometryError) {
        throw error(label + ' contains invalid GeoJSON. ' + geometryError.message.replace('CelovendoCoverage: ', ''));
      }
    }

    function load() {
      if (loadPromise) return loadPromise;
      if (configurationError) {
        loadPromise = Promise.reject(configurationError);
        return loadPromise;
      }
      try {
        var guatemalaSource = sourceForZone('guatemala');
        var xelaSource = sourceForZone('xela');
        var country = countrySource();
        loadPromise = Promise.all([
          loadSource(guatemalaSource, 'Guatemala zone'),
          loadSource(xelaSource, 'Xela zone'),
          loadSource(country, 'country municipalities')
        ]).then(function(results) {
          var guatemala = verifyFeatureCollection(results[0], 'Guatemala zone');
          var xela = verifyFeatureCollection(results[1], 'Xela zone');
          // Municipal boundaries are a large, pre-existing national dataset.
          // Validate every coordinate/ring structurally, but do not run the
          // stricter delivery-zone topology sweep across that source: several
          // official boundary exports contain harmless shared/touching edges.
          var countryCollection = verifyFeatureCollection(results[2], 'country municipalities', false);
          var stores = (config.stores && typeof config.stores === 'object') ? config.stores : {};
          var safeStores = {};
          var storeErrors = {};
          ['guatemala', 'xela'].forEach(function(name) {
            var store = stores[name];
            // Number('') is zero, so reject empty editor fields before
            // coercion. Store coordinates are display-only and must never
            // prevent an otherwise valid coverage classification.
            if (!store || !hasCustomSource(store.lng) || !hasCustomSource(store.lat) ||
                !validPoint(Number(store.lng), Number(store.lat))) {
              safeStores[name] = null;
              storeErrors[name] = 'Missing or invalid store coordinates for "' + name + '".';
              return;
            }
            safeStores[name] = { lat: Number(store.lat), lng: Number(store.lng) };
          });
          loaded = {
            guatemala: guatemala,
            xela: xela,
            stores: safeStores,
            storeErrors: storeErrors,
            country: countryCollection,
            countryIndex: countryCollection.features.map(function(feature) {
              return { geometry: feature.geometry, bounds: geometryBounds(feature.geometry) };
            })
          };
          return {
            guatemala: loaded.guatemala,
            xela: loaded.xela,
            stores: loaded.stores,
            storeErrors: loaded.storeErrors
          };
        });
      } catch (loadError) {
        loadPromise = Promise.reject(loadError);
      }
      return loadPromise;
    }

    function coverageFor(lat, lng) {
      if (!validPoint(lng, lat)) {
        return Promise.resolve({ delivery_method: 'pendiente', delivery_zone: 'desconocida', delivery_coverage_status: 'unknown' });
      }
      return load().then(function() {
        // Cargo Expreso is only a verified result for a point in the supplied
        // country geometry. A coordinate abroad is never treated as "outside
        // the messenger polygon".
        if (!containsCountry(lng, lat, loaded.countryIndex)) {
          return { delivery_method: 'pendiente', delivery_zone: 'desconocida', delivery_coverage_status: 'unknown' };
        }
        var inGuatemala = contains(lng, lat, loaded.guatemala);
        var inXela = contains(lng, lat, loaded.xela);
        if (inGuatemala || inXela) {
          return {
            delivery_method: 'mensajero',
            delivery_zone: inGuatemala && inXela ? 'ambas' : (inGuatemala ? 'guatemala' : 'xela'),
            delivery_coverage_status: 'verified'
          };
        }
        return { delivery_method: 'cargo_expreso', delivery_zone: 'fuera', delivery_coverage_status: 'verified' };
      });
    }

    function cleanLocation(input) {
      var location = {};
      input = input || {};
      Object.keys(input).forEach(function(key) {
        if (key !== 'coverage' && key !== 'delivery_method' && key !== 'delivery_zone' && key !== 'delivery_coverage_status') location[key] = input[key];
      });
      location.lat = Number(input.lat);
      location.lng = Number(input.lng);
      location.confirmed = input.confirmed === true;
      location.precise = input.precise === true;
      return location;
    }

    function invalidate(location) {
      var currentRevision = ++revision;
      var cleaned = location ? cleanLocation(location) : null;
      if (cleaned) {
        delete cleaned.lat;
        delete cleaned.lng;
      }
      state = freshState(cleaned, currentRevision);
      attributesByRevision[currentRevision] = cartAttributes(null, state.coverage);
      readyByRevision[currentRevision] = Promise.resolve(getState());
      queueSave(currentRevision);
      notify();
      return readyByRevision[currentRevision];
    }

    function setDestination(location) {
      var cleaned = cleanLocation(location);
      if (!cleaned.confirmed || !cleaned.precise || !validPoint(cleaned.lng, cleaned.lat)) return invalidate(cleaned);

      var currentRevision = ++revision;
      state = freshState(cleaned, currentRevision);
      notify();
      var ready = coverageFor(cleaned.lat, cleaned.lng).then(function(coverage) {
        commit(currentRevision, cleaned, coverage);
        return getState();
      }).catch(function() {
        // Loading errors must be visible as unverified, but they must not
        // replace custom GeoJSON with fallbacks or mislabel Cargo Expreso.
        var unknown = { delivery_method: 'pendiente', delivery_zone: 'desconocida', delivery_coverage_status: 'unknown' };
        commit(currentRevision, cleaned, unknown);
        return getState();
      });
      readyByRevision[currentRevision] = ready;
      return ready;
    }

    function waitForSave(currentRevision) {
      return new Promise(function(resolve, reject) {
        function check() {
          if (lastSavedRevision >= currentRevision) return resolve();
          if (saveErrors[currentRevision]) return reject(saveErrors[currentRevision]);
          saveWaiters.push(check);
        }
        check();
      });
    }

    function ensureSaved() {
      function saveNewest() {
        var target = revision;
        var ready = readyByRevision[target] || Promise.resolve();
        return ready.then(function() {
          // Destination changed while the previous classification was loading.
          if (target !== revision) return saveNewest();
          // A checkout can be reached before either location event has fired.
          // Persist an explicit pending state in that case rather than leaving
          // old coverage attributes from a previous browser interaction.
          if (!attributesByRevision[target]) {
            attributesByRevision[target] = cartAttributes(state.location, state.coverage);
            queueSave(target);
          }
          if (saveErrors[target]) {
            delete saveErrors[target];
            queueSave(target); // explicit retry; failures remain retriable
          }
          return waitForSave(target).then(function() {
            return target === revision ? getState() : saveNewest();
          });
        });
      }
      return saveNewest();
    }

    function getState() {
      return clone(state);
    }

    /*
     * "Zona de entrega con mensajero" sobre el borde de cobertura más cercano al
     * centro del mapa. Solo se ve cuando ese borde entra en pantalla (al alejar
     * el mapa o con el pin cerca del límite) y no tapa el pin del centro.
     */
    function zoneLabel(maps, map, rings, text) {
      if (!maps.OverlayView || !maps.LatLng || !rings.length || !doc || !doc.createElement) return null;
      var overlay = new maps.OverlayView();
      var node = null;
      var position = null;
      overlay.onAdd = function() {
        node = doc.createElement('div');
        node.className = 'cv-zone-label';
        node.textContent = text;
        node.style.display = 'none';
        var panes = overlay.getPanes();
        (panes.floatPane || panes.overlayLayer).appendChild(node);
      };
      overlay.draw = function() {
        if (!node) return;
        var projection = overlay.getProjection();
        var center = map.getCenter();
        if (!position || !projection || !center) {
          node.style.display = 'none';
          return;
        }
        var point = projection.fromLatLngToDivPixel(new maps.LatLng(position.lat, position.lng));
        var middle = projection.fromLatLngToDivPixel(center);
        var clearOfPin = Math.abs(point.x - middle.x) > 90 || Math.abs(point.y - middle.y) > 45;
        node.style.left = point.x + 'px';
        node.style.top = point.y + 'px';
        node.style.display = clearOfPin ? '' : 'none';
      };
      overlay.onRemove = function() {
        if (node && node.parentNode) node.parentNode.removeChild(node);
        node = null;
      };
      function update() {
        var center = map.getCenter();
        var bounds = map.getBounds && map.getBounds();
        var point = center ? closestEdgePoint(rings, { lat: center.lat(), lng: center.lng() }) : null;
        position = point && bounds && bounds.contains(new maps.LatLng(point.lat, point.lng)) ? point : null;
        overlay.draw();
      }
      var listener = maps.event && maps.event.addListener ? maps.event.addListener(map, 'idle', update) : null;
      overlay.setMap(map);
      return {
        remove: function() {
          if (listener && maps.event.removeListener) maps.event.removeListener(listener);
          overlay.setMap(null);
        }
      };
    }

    function attachGoogleMap(map) {
      return load().then(function(data) {
        if (!map || !windowRef.google || !windowRef.google.maps) throw error('Google Maps is not available.');
        var maps = windowRef.google.maps;
        var existing = overlaysByMap.get ? overlaysByMap.get(map) : null;
        if (existing) existing.clear();
        var result = { polygons: [], markers: [], label: null };
        var outerRings = [];
        // Las dos zonas con el mismo color: el mapa ya no lleva leyenda. La
        // etiqueta del borde dice qué es (zoneLabel).
        function drawZone(collection, color) {
          collection.features.forEach(function(feature) {
            var groups = feature.geometry.type === 'Polygon' ? [feature.geometry.coordinates] : feature.geometry.coordinates;
            groups.forEach(function(polygon) {
              var paths = polygon.map(function(ring) {
                return ring.map(function(point) { return { lng: point[0], lat: point[1] }; });
              });
              if (paths[0]) outerRings.push(paths[0]);
              var overlay = new maps.Polygon({
                paths: paths,
                strokeColor: color,
                strokeOpacity: 0.85,
                strokeWeight: 2,
                fillColor: color,
                fillOpacity: 0.14,
                map: map
              });
              result.polygons.push(overlay);
            });
          });
        }
        drawZone(data.guatemala, ZONE_COLOR);
        drawZone(data.xela, ZONE_COLOR);
        result.label = zoneLabel(maps, map, outerRings, ZONE_LABEL);
        ['guatemala', 'xela'].forEach(function(name) {
          if (!data.stores[name]) return;
          result.markers.push(new maps.Marker({
            map: map,
            position: data.stores[name],
            title: name === 'xela' ? 'Tienda Xela' : 'Tienda Guatemala'
          }));
        });
        result.clear = function() {
          result.polygons.concat(result.markers).forEach(function(item) {
            if (item && item.setMap) item.setMap(null);
          });
          if (result.label) result.label.remove();
        };
        result.storeErrors = clone(data.storeErrors || {});
        if (overlaysByMap.set) overlaysByMap.set(map, result);
        return result;
      });
    }

    function restoreCookieLocation() {
      if (!doc || !doc.cookie) return;
      var match = doc.cookie.match(/(?:^|;\s*)cv_location=([^;]*)/);
      if (!match) return;
      try {
        // Only coordinates are restored; any old zone/classification in the
        // cookie is intentionally ignored and recalculated from current data.
        var oldLocation = JSON.parse(decodeURIComponent(match[1]));
        setDestination(oldLocation);
      } catch (ignored) {}
    }

    function onLocationEvent(event) {
      var location = event && event.detail;
      if (location && location.location) location = location.location;
      // location-service emits this acknowledgement after copying an
      // authoritative coverage snapshot into cv_location. Re-forwarding it
      // would start a new pending/verified classification cycle.
      if (location && location.coverageSynced === true) return;
      setDestination(location || null);
    }

    if (doc && doc.addEventListener) {
      doc.addEventListener('cv:location-changed', onLocationEvent);
      doc.addEventListener('cv:location-ready', onLocationEvent);
      if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', restoreCookieLocation);
      else restoreCookieLocation();
    }

    return {
      load: load,
      classify: coverageFor,
      contains: contains,
      validate: validation,
      validateGeometry: validateGeometry,
      validateGeoJSON: validateGeoJSON,
      geometry: {
        contains: contains,
        validate: validation,
        validateGeometry: validateGeometry,
        validateGeoJSON: validateGeoJSON,
        normalizeFeatureCollection: asFeatureCollection
      },
      setDestination: setDestination,
      invalidate: invalidate,
      getState: getState,
      ensureSaved: ensureSaved,
      attachGoogleMap: attachGoogleMap
    };
  }

  // Create immediately so location events emitted by the existing location
  // service are observed even before a cart component calls this API.
  var defaultEngine = createCoverageEngine();
  function getDefaultEngine() {
    return defaultEngine;
  }

  // Browser consumers use this singleton. create() is also intentionally
  // public to make geometry and save ordering testable without a DOM.
  var publicApi = {
    create: createCoverageEngine,
    contains: contains,
    validate: validation,
    validateGeometry: validateGeometry,
    validateGeoJSON: validateGeoJSON,
    geometry: {
      closestEdgePoint: closestEdgePoint,
      contains: contains,
      validate: validation,
      validateGeometry: validateGeometry,
      validateGeoJSON: validateGeoJSON,
      normalizeFeatureCollection: asFeatureCollection
    }
  };
  ['load', 'classify', 'setDestination', 'invalidate', 'getState', 'ensureSaved', 'attachGoogleMap'].forEach(function(method) {
    publicApi[method] = function() {
      return getDefaultEngine()[method].apply(null, arguments);
    };
  });
  return publicApi;
});